import React from 'react';
import { IParticipant } from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { IInitMessage } from '../Pureweb/interfaces';
import { StreamingProviders } from '../../../providers/MytaverseEventProvider/interfaces';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { IEvent, IPixelStreamingClient } from '@interfaces/event';
import { IStartStreamSessionOutput } from '@interfaces/gamecast';
import { AnalyticsType } from '@interfaces/analytics';

declare global {
  interface Window {
    gameliftstreams: {
      GameLiftStreams: new (args: IGameCastArgs) => IGameCast;
      setLogLevel: (level: 'none' | 'debug') => void;
      VERSION: string;
    };
  }
}

export interface IGamecastStreamSession {
  Status: string;
  SignalResponse: string;
  WebSdkProtocolUrl: string;
  Arn?: string;
  StreamGroupId?: string;
  ApplicationArn?: string;
  UserId?: string;
}

export interface GameCastStreamSession {
  attachClientInput: () => void;
  closeConnection: () => void;
  detachClientInput: () => void;
  id: string;
  stats: {
    getRtcStatsReport: () => void;
  };
}

export interface GameCastInstance {
  stream: (signalResponse: {
    signal_response: string;
    gamecast_protocol_url: string;
  }) => Promise<GameCastStreamSession>;
  generateSignalRequest: () => { signal_request: string };
}

interface IReconnectGamecastProps {
  participantId: string;
  websocketSessionId: string;
  initMessageHandler: () => Promise<IInitMessage | null>;
  region: string;
  launchArgs: string[] | null;
  sessionKey?: string;
}

export interface IUseGamecast {
  isStreamLoading: boolean;
  isStreamLoaded: boolean;
  setIsStreamLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStreamLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  closeGamecastStream: () => void;
  abortGamecastConnection: () => void;
  gamecastClientRef: React.MutableRefObject<IGameCast | null>;
  processActiveStreamSession: (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => Promise<{
    isStarted: boolean;
    isAborted: boolean;
  }>;
  trackAnalytics: (type: AnalyticsType, payload?: any) => void;
  sendMytaverseActions: (currentEventParticipant: IEventParticipant) => void;
  handleReconnectGamecast: (data: IReconnectGamecastProps) => Promise<void>;
  initializeGamecastStream: (
    participantId: string,
    websocketSessionId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    launchArgs: string[] | null,
  ) => Promise<
    | {
        isStarted: boolean;
      }
    | { isAborted: boolean; gamecastRTCStatsInterval: any }
  >;

  initGameliftStreamsSessionStatusTimeout: () => void;
  clearGameliftStreamsSessionStatusTimeout: () => void;

  setReconnectGamecast: React.Dispatch<React.SetStateAction<boolean>>;
  resetReconnectGamecastAttempts: () => void;
}

export interface IUseGamecastProps {
  currentEventId: string | null | undefined;
  currentRoom: IRoom | null | undefined;
  trackAnalytics: (type: AnalyticsType, payload?: any) => void;
  gameSound: number;
  currentParticipant: IParticipant | undefined;
  websocketSessionId: string;
  setGameCastStreamId: React.Dispatch<React.SetStateAction<string>>;
  setGameCastStreamRegion: React.Dispatch<React.SetStateAction<string>>;
  initMessageHandler: () => Promise<IInitMessage | null>;
  gameCastStreamRequestSent: boolean;
  setGameCastStreamRequestSent: React.Dispatch<React.SetStateAction<boolean>>;
  setInitMessageSent: React.Dispatch<React.SetStateAction<boolean>>;
  gameCastStreamId: string;
  gameCastStreamRegion: string;
  setStreamingProvider: React.Dispatch<
    React.SetStateAction<StreamingProviders | null>
  >;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentRoom: React.Dispatch<React.SetStateAction<IRoom | null>>;
  setCurrentParticipant: React.Dispatch<React.SetStateAction<IParticipant | undefined>>;
  isGamecastCrash: boolean;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  currentEventParticipant: IEventParticipant | null;
  currentEvent: IEvent | undefined;
  generateNewGameliftStreamsSessionKey: () => string;
  isGamecastTimeout: boolean;
  setIsGamecastTimeout: React.Dispatch<React.SetStateAction<boolean>>;
  pixelStreamingClient: IPixelStreamingClient | null;
  externalKeyboardConnected: boolean;
  setShowExternalKeyboardOverlay: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IGameCast {
  id: string;

  enableMicrophone(constraints?: any): Promise<void>;

  generateSignalRequest(): Promise<string>;

  processSignalResponse(signalResponse: string): Promise<void>;

  attachInput(): void;

  detachInput(): void;

  getVideoRTCStats(): Promise<RTCStatsReport>;

  getAudioRTCStats(): Promise<RTCStatsReport>;

  close(): void;

  processKeyboardEvent(e: KeyboardEvent): boolean;

  processMouseEvent(e: MouseEvent): boolean;

  addGamepad(gamepad: Gamepad): boolean;

  removeGamepad(gamepad: Gamepad): boolean;

  processGamepads(): boolean;

  sendApplicationMessage(message: Uint8Array): boolean;
}

export interface IGameCastArgs {
  videoElement: HTMLVideoElement;
  audioElement: HTMLAudioElement;
  inputConfiguration?: InputConfiguration;
  clientConnection?: IClientConnection;
  streamConfiguration?: GamecastStreamConfiguration;
}

export interface GamecastStreamConfiguration {
  enableAudio?: boolean;
  maximumKbps?: number;
  iceServers?: () => Promise<GamecastIceServerConfiguration[]>;
}

export type GamecastIceServerConfiguration = {
  urls?: string | string[];
  url?: string;
  username?: string;
  credential?: string;
};

interface InputConfiguration {
  autoKeyboard?: boolean;
  autoMouse?: boolean;
  autoGamepad?: boolean;
  autoPointerLock?: boolean | 'fullscreen';
  keyboardFilter?: null | ((event: KeyboardEvent) => boolean);
  mouseFilter?: null | ((event: MouseEvent) => boolean);
  gamepadFilter?: null | ((event: Gamepad) => boolean | GamepadFilterResult);
  hapticFeedback?: boolean;
  setCursor?: boolean | 'visibility' | ((cursorStyle: string) => void);
  resetOnDetach?: boolean;
  trackWindowFocus?: boolean;
}

export enum IGamecastServerDisconnectReason {
  Idle = 'idle',
  Terminated = 'terminated',
  Disconnected = 'disconnected',
}

interface GamepadFilterResult {
  suppressButtonIndexes: readonly number[];
}

interface IClientConnection {
  connectionState?: (state: string) => void;
  channelError?: (e: any) => void;
  serverDisconnect?: (reasoncode: string) => void;
  applicationMessage?: (message: Uint8Array) => void;
}

export interface IGamecastVideoStats {
  bytesReceived: number;
  codecId: string;
  decoderImplementation: string;
  estimatedPlayoutTimestamp: number;
  firCount: number;
  frameHeight: number;
  frameWidth: number;
  framesAssembledFromMultiplePackets: number;
  framesDecoded: number;
  framesDropped: number;
  framesPerSecond: number;
  framesReceived: number;
  freezeCount: number;
  googTimingFrameInfo: string;
  headerBytesReceived: number;
  id: string;
  jitter: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
  jitterBufferMinimumDelay: number;
  jitterBufferTargetDelay: number;
  keyFramesDecoded: number;
  kind: string;
  lastPacketReceivedTimestamp: number;
  mediaType: string;
  mid: string;
  nackCount: number;
  packetsLost: number;
  packetsReceived: number;
  pauseCount: number;
  pliCount: number;
  powerEfficientDecoder: boolean;
  remoteId: string;
  retransmittedBytesReceived: number;
  retransmittedPacketsReceived: number;
  rtxSsrc: number;
  ssrc: number;
  timestamp: number;
  totalAssemblyTime: number;
  totalDecodeTime: number;
  totalFreezesDuration: number;
  totalInterFrameDelay: number;
  totalPausesDuration: number;
  totalProcessingDelay: number;
  totalSquaredInterFrameDelay: number;
  trackIdentifier: string;
  transportId: string;
  type: string;
  transferSpeed: string;
  connectionSpeed: number;
  avgConnectionSpeed: number;
  minSamplesCollected: boolean;
}
